import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.token) localStorage.setItem("bf_session_token", data.token);
      setUser(data.user);
      toast.success("Password updated — you're signed in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bf-glow p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-2.5"><img src="/logo.png" alt="OWEverit" className="h-8 w-8 rounded-lg" /><div className="font-display font-extrabold text-lg">OWEverit</div></div>
        <h1 className="font-display text-xl font-bold mt-4 flex items-center gap-2"><KeyRound className="h-5 w-5 text-teal-500" /> Set a new password</h1>
        {!token ? (
          <p className="text-sm text-rose-500 mt-3" data-testid="reset-no-token">This link is missing its token. Please request a new reset email.</p>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">New password</span>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus data-testid="reset-password"
                className="mt-1 w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40" />
              <span className="text-[10px] text-muted-foreground mt-1 block">At least 8 characters, with a letter and a number</span>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Confirm password</span>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="reset-confirm"
                className="mt-1 w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40" />
            </label>
            {error && <p className="text-xs text-rose-500" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="reset-submit"
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Update password <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        )}
        <button onClick={() => navigate("/login")} className="mt-4 text-xs text-muted-foreground hover:text-foreground" data-testid="reset-back">← Back to sign in</button>
      </div>
    </div>
  );
}
